import { JanusJS } from 'lib/janus';
import { RTCClient, JanusConnection } from 'utils/webrtc';
import { SubscribeStreamList } from 'features/streaming/types';
import * as Sentry from '@sentry/react';
import { formatErrorForLogging, logger } from 'utils/logger';

const CONNECTING_TIMEOUT_MS = 15_000;

export enum VideoroomConnectionState {
  created,
  connecting,
  connected,
  disconnected,
  disconnecting,
}

export class ControlledReceivingHandle {
  janusPlugin: JanusJS.PluginHandle;

  handle: string;

  connection: JanusConnection;

  privateId?: number;

  connectionState: VideoroomConnectionState = VideoroomConnectionState.created;

  pendingSubscriptions: SubscribeStreamList = [];

  private connectingTimeoutId: number = 0;

  constructor(janusPlugin: JanusJS.PluginHandle, handle: string, connection: JanusConnection) {
    this.janusPlugin = janusPlugin;
    this.handle = handle;
    this.connection = connection;
  }

  get canSubscribe() {
    return [VideoroomConnectionState.created, VideoroomConnectionState.connected].includes(
      this.connectionState
    );
  }

  setConnectionState = (state: VideoroomConnectionState) => {
    this.connectionState = state;

    if (state === VideoroomConnectionState.connecting) {
      this.startConnectingTimeout();
    } else {
      this.clearConnectingTimeout();
    }

    if (this.canSubscribe) {
      this.processQueue();
    }
  };

  enqueueSubscription = (streams: SubscribeStreamList) => {
    this.pendingSubscriptions = this.pendingSubscriptions.concat(streams);
  };

  processQueue = () => {
    if (!this.pendingSubscriptions.length) {
      return;
    }

    if (RTCClient.receivingFeed.receiver?.plugin !== this) {
      logger
        .remote({ system: true, capture: 'streaming' })
        .warn(
          `[STALE-HANDLE] Refusing to flush the queued subscriptions of handle ${this.handle} — it was superseded by a newer receiving feed.`,
          {
            connectionState: VideoroomConnectionState[this.connectionState],
            liveHandle: RTCClient.receivingFeed.receiver?.plugin?.handle,
            streams: this.pendingSubscriptions.map(({ feed: feedId, mid }) => ({ feedId, mid })),
          }
        );

      return;
    }

    const streams = [...this.pendingSubscriptions];

    this.pendingSubscriptions = [];

    RTCClient.receivingFeed.requestSubscription(this.connection.handle, streams).catch((error) => {
      if (RTCClient.supressErrors) {
        return;
      }

      const message =
        '[QUEUE-FLUSH] The queued subscriptions were dropped, nothing will retry them';
      const context = {
        handle: this.handle,
        connectionHandle: this.connection.handle,
        connectionState: VideoroomConnectionState[this.connectionState],
        streams: streams.map(({ feed: feedId, mid }) => ({ feedId, mid })),
      };

      logger.remote({ system: true, capture: 'streaming' }).error(message, context);

      Sentry.captureException(new Error(message, { cause: error }), {
        tags: { component: 'ControlledReceivingHandle', operation: 'queue-flush' },
        contexts: { queuedSubscription: { ...context, ...formatErrorForLogging(error) } },
      });
    });
  };

  private startConnectingTimeout = () => {
    this.clearConnectingTimeout();

    this.connectingTimeoutId = window.setTimeout(() => {
      if (this.connectionState === VideoroomConnectionState.connecting) {
        logger
          .remote({ system: true, capture: 'streaming' })
          .warn(
            `[CONNECTING-TIMEOUT] Subscribing handle ${this.handle} stuck in connecting state for ${CONNECTING_TIMEOUT_MS}ms, resetting to created`
          );

        this.setConnectionState(VideoroomConnectionState.created);
      }
    }, CONNECTING_TIMEOUT_MS);
  };

  private clearConnectingTimeout = () => {
    if (this.connectingTimeoutId) {
      window.clearTimeout(this.connectingTimeoutId);
      this.connectingTimeoutId = 0;
    }
  };
}
